import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { resetStep, setIsComingFromNonUserRoute } from "../../../redux/slice/stepSlice";
import { generatePDFs } from "../../../services/printService";
import { setCoverImages, setImages } from "../../../redux/slice/sketchSlice";

const ThankYou = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const activeBookId = state?.bookId

  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const orderId = state?.orderId;
  const paymentIntent = state?.paymentIntent;

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const runGenPDF = async () => {
    if (!activeBookId) return;

    setIsGeneratingPDF(true);
    setPdfError(null);

    try {
      const imageUrls = getAllImageUrls();
      console.log(`📖 Generating PDF for book ${activeBookId} with ${imageUrls.length} images`);

      const blob = await generatePDFs({
        imageUrls: state.imageUrls,
        bookId: activeBookId
      });
      
      setPdfBlob(blob);
      
      // Create object URL for embedding
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      
      setPdfError(null);
    } catch (err) {
      console.error('❌ PDF generation failed:', err);
      setPdfError(err.message || 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  }

  // Redirect if no payment info
  if (!orderId || !paymentIntent) {
    return <Navigate to="/" replace />;
  }

  // Get all AI-generated image URLs
  const getAllImageUrls = () => state.imageUrls || []

  const navigateToDashboard = () => {
    dispatch(resetStep());
    dispatch(setCoverImages([]));
    dispatch(setImages([]));
    dispatch(setIsComingFromNonUserRoute(true));
    navigate('/dashboard');
  };

  const handlePrintPDF = () => {
    if (pdfUrl) {
      // Open PDF in new window and trigger print
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded shadow-lg text-center space-y-6">
      {/* Success Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-green-600">🎉 Thank You!</h1>
        <p className="text-slate-700">Your payment was successful.</p>
      </div>

      {/* Payment Details */}
      <div className="bg-gray-50 p-4 rounded-lg shadow-sm text-left space-y-2">
        <p>
          <strong>Order ID:</strong> {orderId}
        </p>
        <p>
          <strong>Payment Transaction ID:</strong> {paymentIntent.transactionId}
        </p>
        <p>
          <strong>Status:</strong> 
          <span className="ml-2 inline-block px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded">
            {paymentIntent.status}
          </span>
        </p>
        <p>
          <strong>Amount:</strong> ${paymentIntent.total} USD
        </p>
      </div>

      {/* PDF Section */}
      {activeBookId && (
        <div className="border-t pt-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">
            📄 Your Print-Ready PDF
          </h2>

          {/* PDF Error */}
          {pdfError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
              <p className="text-red-800 font-semibold">Failed to generate PDF</p>
              <p className="text-red-600 text-sm mt-1">{pdfError}</p>
              <button
                onClick={runGenPDF}
                className="mt-3 text-sm text-red-700 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          )}

          {/* PDF Success with Embedded Viewer */}
          {pdfBlob && pdfUrl && !pdfError && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-center gap-2 text-blue-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">PDF Ready!</span>
              </div>
              
              <p className="text-sm text-slate-600">
                Size: {(pdfBlob.size / 1024 / 1024).toFixed(2)} MB • {getAllImageUrls().length} images • Saddle-stitch ready
              </p>

              {/* Embedded PDF Viewer */}
              <div className="bg-white rounded-lg overflow-hidden shadow-md">
                <iframe
                  src={pdfUrl}
                  className="w-full h-[600px] border-0"
                  title="PDF Preview"
                />
              </div>

              {/* Print Button */}
              <div className="flex justify-center pt-2">
                <button
                  onClick={handlePrintPDF}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print PDF
                </button>
              </div>
            </div>
          )}

          {/* Generate PDF Button - shows when no PDF exists */}
          {!isGeneratingPDF && !pdfBlob && !pdfError && (
            <button
              onClick={runGenPDF}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
            >
              Generate PDF
            </button>
          )}

          {/* Loading state */}
          {isGeneratingPDF && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-2 text-blue-800">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Generating your PDF...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <button
        onClick={navigateToDashboard}
        className="mt-6 px-6 py-2 bg-md-pink text-white rounded-lg hover:bg-pink-700 transition"
      >
        Back to Dashboard
      </button>
    </div>
  );
};

export default ThankYou;